import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PeopleAlt as PeopleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { usePermissions } from '../../../contexts/PermissionsContext';

const formatDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${date} · ${time}`;
};

const SocialEventListPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { canManageSocialEvents } = usePermissions();
  const canManageEvents = canManageSocialEvents();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includePast, setIncludePast] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.listTenantSocialEvents(includePast);
      setEvents(data || []);
    } catch {
      setError('Failed to load social events.');
    } finally {
      setLoading(false);
    }
  }, [includePast]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await unifiedApi.deleteSocialEvent(deleteTarget.id);
      setDeleteTarget(null);
      fetchEvents();
    } catch {
      setError('Failed to delete event.');
    } finally {
      setDeleting(false);
    }
  };

  const scopeChip = (ev) => {
    if (!ev.leagueId) return <Chip size="small" label="Tenant-wide" color="primary" clickable={false} onClick={() => {}} />;
    if (ev.teamId) return <Chip size="small" label={`Team: ${ev.teamName ?? ''}`} clickable={false} onClick={() => {}} />;
    return <Chip size="small" label={`League: ${ev.leagueName ?? ''}`} color="info" clickable={false} onClick={() => {}} />;
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Social Events</Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <FormControlLabel
              control={<Switch checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />}
              label="Show past"
            />
            <Button startIcon={<RefreshIcon />} onClick={fetchEvents} disabled={loading} fullWidth={isMobile}>
              Refresh
            </Button>
            {canManageEvents && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to="/admin/social-events/new"
                fullWidth={isMobile}
              >
                Create event
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No social events yet. Create your first one to get started.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {events.map((ev) => (
            <Paper
              key={ev.id}
              sx={{ p: 2, cursor: 'pointer' }}
              onClick={() => navigate(`/admin/social-events/${ev.id}/rsvps`)}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-word', flex: 1 }}>
                    {ev.title}
                  </Typography>
                  {scopeChip(ev)}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(ev.start)}
                </Typography>
                {ev.location && (
                  <Typography variant="body2" color="text.secondary">
                    {ev.location}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ pt: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Attending: {ev.attendingCount ?? '—'}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View RSVPs">
                      <IconButton size="small" component={Link} to={`/admin/social-events/${ev.id}/rsvps`} aria-label={`View RSVPs for ${ev.title}`}>
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManageEvents && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" component={Link} to={`/admin/social-events/${ev.id}/edit`} aria-label={`Edit ${ev.title}`}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(ev)} aria-label={`Delete ${ev.title}`}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Attending</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((ev) => (
                  <TableRow
                    key={ev.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/social-events/${ev.id}/rsvps`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{ev.title}</Typography>
                    </TableCell>
                    <TableCell>{formatDateTime(ev.start)}</TableCell>
                    <TableCell>{ev.location || '—'}</TableCell>
                    <TableCell>{scopeChip(ev)}</TableCell>
                    <TableCell>{ev.attendingCount ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View RSVPs">
                          <IconButton size="small" component={Link} to={`/admin/social-events/${ev.id}/rsvps`} aria-label={`View RSVPs for ${ev.title}`}>
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManageEvents && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" component={Link} to={`/admin/social-events/${ev.id}/edit`} aria-label={`Edit ${ev.title}`}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(ev)} aria-label={`Delete ${ev.title}`}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete social event?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteTarget?.title}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default SocialEventListPage;
